import { Injectable } from "@angular/core";
import { interval, Subscription, combineLatest } from "rxjs";
import { ApiService } from "./api.service";
import { LocationStateService } from "../state/location-state.service";
import { LocationStateQuery } from "../state/location-state.query";
import { filter, take } from "rxjs/operators";
import { ExternalProviderConfig } from "source/app/features/dashboard/widgets/external-provider.model";
import { RaygunService } from "./raygun.service";

@Injectable({
	providedIn: "root",
})
export class ExternalDataPollingService {
	constructor(
		private apiService: ApiService,
		private locationStateService: LocationStateService,
		private locationStateQuery: LocationStateQuery,
		private raygunService: RaygunService,
	) {}

	private pollingSubscriptions: Map<string, Subscription> = new Map();
	private readonly POLLING_INTERVAL = 120000; // 2 minutes in milliseconds

	startPolling(providerConfig: ExternalProviderConfig): void {
		// Only start polling if Safari AI is enabled and the appropriate features are allowed
		combineLatest([this.locationStateQuery.externalDataSettings$, this.locationStateQuery.operator$])
			.pipe(
				take(1),
				filter(([safariEnabled, operator]) => safariEnabled && operator !== undefined),
			)
			.subscribe(() => {
				const subscription = interval(this.POLLING_INTERVAL).subscribe(() => {
					this.fetchExternalData(providerConfig);
				});

				this.pollingSubscriptions.set(providerConfig.tag, subscription);
			});
	}

	stopPolling(providerConfig: ExternalProviderConfig): void {
		const subscription = this.pollingSubscriptions.get(providerConfig.tag);
		if (subscription) {
			subscription.unsubscribe();
			this.pollingSubscriptions.delete(providerConfig.tag);
		}
	}

	private async fetchExternalData(providerConfig: ExternalProviderConfig): Promise<void> {
		try {
			const [dispatchData, queueTimeData] = await Promise.all([
				this.apiService.getExternalDispatch(providerConfig.tag),
				this.apiService.getExternalQueueTime(providerConfig.tag),
			]);

			this.locationStateService.updateExternalDispatch(dispatchData);
			this.locationStateService.updateExternalQueueTime(queueTimeData);
		} catch (error: unknown) {
			// Skip logging for connection and timeout errors
			const isHttpError = typeof error === "object" && error !== null && "status" in error;
			if (isHttpError && (error.status === 0 || error.status === 504)) {
				throw error;
			}

			if (error instanceof Error) {
				this.raygunService.send(error, {});
			} else {
				this.raygunService.send(new Error("An unknown error occured during updateLocationStatus."), {
					error: error,
				});
			}

			throw error;
		}
	}
}
